/*
  predictive-search.js - NOTFORTOMORROW OS2.0 search enhancement (4th-surface adjacent, tiny).
  Progressive enhancement ONLY. The host is a real <form action="/search" method="get"> that
  fully works with JS OFF (submit -> /search?q=...). This module upgrades it into an accessible
  combobox: fetch Shopify's native /search/suggest results, render them into a listbox, wire
  ARIA combobox semantics + keyboard nav, and announce result counts. No framework, no deps.

  It enhances any <predictive-search> element that wraps:
    [data-predictive-input]    the <input type="search">
    [data-predictive-results]  the empty results container (role="listbox" host)
  and reads endpoint + section id from data-attributes set in predictive-search.liquid.

  Honesty: results are 100% live Shopify search results. Nothing is fabricated. If the fetch
  fails or returns nothing, the listbox closes quietly and the native submit still works.
*/

const SUGGEST_PATH = '/search/suggest';

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-predictive-input]');
    this.results = this.querySelector('[data-predictive-results]');
    this.reset = this.querySelector('[data-predictive-reset]');
    this.statusEl = this.querySelector('[data-predictive-status]');
    if (!this.input || !this.results) return;

    this.minChars = parseInt(this.dataset.minChars || '2', 10);
    this.limit = parseInt(this.dataset.limit || '6', 10);
    this.sectionId = this.dataset.sectionId || '';
    this.controller = null;
    this.debounce = null;
    this.open = false;

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.results.id);
    this.input.setAttribute('autocomplete', 'off');

    this._onInput = this._onInput.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onDocClick = this._onDocClick.bind(this);

    this.input.addEventListener('input', this._onInput);
    this.input.addEventListener('keydown', this._onKeydown);
    this.input.addEventListener('focus', () => { if (this.input.value.length >= this.minChars) this._search(this.input.value); });
    document.addEventListener('click', this._onDocClick);

    if (this.reset) {
      this.reset.addEventListener('click', () => {
        this.input.value = '';
        this._close();
        this.input.focus();
        this._toggleReset();
      });
    }
    this._toggleReset();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
  }

  _toggleReset() {
    if (this.reset) this.reset.hidden = this.input.value.length === 0;
  }

  _onInput() {
    this._toggleReset();
    const q = this.input.value.trim();
    clearTimeout(this.debounce);
    if (q.length < this.minChars) { this._close(); return; }
    this.debounce = setTimeout(() => this._search(q), 220);
  }

  async _search(q) {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    const params = new URLSearchParams({
      q,
      'resources[type]': 'product',
      'resources[limit]': String(this.limit),
      'resources[options][unavailable_products]': 'last'
    });
    if (this.sectionId) params.set('section_id', this.sectionId);

    try {
      const res = await fetch(`${SUGGEST_PATH}?${params.toString()}`, { signal: this.controller.signal });
      if (!res.ok) { this._close(); return; }
      const html = await res.text();
      // The section render returns full markup; extract just the results list.
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fragment = doc.querySelector('[data-predictive-results-inner]');
      this.results.innerHTML = fragment ? fragment.innerHTML : '';
      this._afterRender();
    } catch (err) {
      if (err.name !== 'AbortError') this._close();
    }
  }

  _afterRender() {
    this.options = Array.from(this.results.querySelectorAll('[role="option"]'));
    const count = this.options.length;
    if (count === 0) {
      // Keep an honest "no results" row if the section rendered one; else close.
      const empty = this.results.querySelector('[data-predictive-empty]');
      if (!empty) { this._close(); return; }
    }
    this.activeIndex = -1;
    this._openList();
    if (this.statusEl) {
      const tpl = this.statusEl.dataset.countTemplate || '{{count}}';
      this.statusEl.textContent = tpl.replace('{{count}}', String(count));
    }
  }

  _openList() {
    this.open = true;
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  _close() {
    this.open = false;
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  }

  _onDocClick(e) {
    if (!this.contains(e.target)) this._close();
  }

  _onKeydown(e) {
    if (e.key === 'Escape') { this._close(); return; }
    if (!this.open || !this.options || this.options.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); this._move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this._move(-1); }
    else if (e.key === 'Enter') {
      if (this.activeIndex >= 0) {
        e.preventDefault();
        const link = this.options[this.activeIndex].querySelector('a');
        if (link) link.click();
      }
      // else: let the native form submit to /search.
    }
  }

  _move(dir) {
    const n = this.options.length;
    this.activeIndex = (this.activeIndex + dir + n) % n;
    this.options.forEach((opt, i) => {
      const active = i === this.activeIndex;
      opt.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active) {
        this.input.setAttribute('aria-activedescendant', opt.id);
        opt.scrollIntoView({ block: 'nearest' });
      }
    });
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
